/**
 * Flight / LDS payable total must include agent handling fee (עמלת סוכן).
 *
 * Persist and backend display_total sometimes stamp priceWithCommission + baggage
 * and skip priceWithHandleFee. Only uplift when the gap is explained by that miss.
 */
import { resolveLdsOrderFinalPriceBreakdown } from "../extractLdsOrderFinalPriceIls.js";
import { resolveAbsoluteAgentHandlingFeeIls } from "../resolveAbsoluteAgentHandlingFeeIls.js";
import { resolveLdsOrderOurPriceBlock } from "./resolveLdsOrderHrCommissionIls.js";

const MONEY_TOL = 0.51;

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function finiteNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function firstPositive(...candidates) {
  for (const value of candidates) {
    const n = finiteNumber(value);
    if (n != null && n > 0) return n;
  }
  return null;
}

function roughlyEqual(a, b, eps = MONEY_TOL) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= eps;
}

function matchesAny(total, candidates) {
  return candidates.some((candidate) => roughlyEqual(total, candidate));
}

function uniquePositives(values) {
  const out = [];
  for (const value of values) {
    const n = finiteNumber(value);
    if (n == null || n < 0) continue;
    if (out.some((existing) => roughlyEqual(existing, n, 0.001))) continue;
    out.push(n);
  }
  return out;
}

/**
 * Managed fare before baggage: PWCCF when it already covers handleFee, else priceWithHandleFee.
 * @param {object|null|undefined} ourPrice
 * @returns {number}
 */
export function resolveLdsManagedPayableBaseIls(ourPrice) {
  const op = ourPrice && typeof ourPrice === "object" ? ourPrice : {};
  const tf =
    op.ourTotalFare && typeof op.ourTotalFare === "object" ? op.ourTotalFare : {};
  const pwc = firstPositive(tf.priceWithCommission, op.priceWithCommission);
  const pwhf = firstPositive(tf.priceWithHandleFee, op.priceWithHandleFee);
  const pwccf = firstPositive(
    tf.priceWithCommissionAndCreditCardFee,
    op.priceWithCommissionAndCreditCardFee,
    op.priceWithCreditCardFee
  );
  const hf = resolveAbsoluteAgentHandlingFeeIls(op);

  if (pwccf != null) {
    if (pwhf != null && pwccf + MONEY_TOL >= pwhf) return pwccf;
    if (hf > 0.009 && pwc != null && roughlyEqual(pwccf, pwc)) {
      return round2((pwhf != null ? pwhf : pwc + hf));
    }
    if (hf > 0.009 && pwhf != null && pwccf < pwhf) {
      const cardOnPwc = pwc != null ? Math.max(0, round2(pwccf - pwc)) : 0;
      return round2(pwhf + cardOnPwc);
    }
    return pwccf;
  }
  if (pwhf != null) return pwhf;
  if (pwc != null) return round2(pwc + Math.max(0, hf));
  return 0;
}

function resolveBaggageIls(order, op, tf) {
  const fromStamp = firstPositive(
    op?.baggagePrice,
    tf?.baggagePrice,
    order?.baggagePrice,
    order?.public_baggage_total_ils
  );
  if (fromStamp != null) return fromStamp;
  const breakdown = resolveLdsOrderFinalPriceBreakdown(order);
  const detected = finiteNumber(breakdown?.baggageTotalDetected);
  return detected != null && detected > 0 ? detected : 0;
}

/**
 * @param {object} p
 * @param {number|null} p.total
 * @param {object|null|undefined} p.order
 * @returns {{ total: number|null, addedHandlingFee: boolean, handlingFeeIls: number, sourceSuffix: string|null }}
 */
export function applyLdsMissingHandlingFeeToPayableTotal({ total, order } = {}) {
  const empty = {
    total: total ?? null,
    addedHandlingFee: false,
    handlingFeeIls: 0,
    sourceSuffix: null,
  };
  if (total == null || !Number.isFinite(Number(total)) || !order || typeof order !== "object") {
    return empty;
  }

  const op = resolveLdsOrderOurPriceBlock(order);
  const tf =
    op.ourTotalFare && typeof op.ourTotalFare === "object" ? op.ourTotalFare : {};
  const hf = resolveAbsoluteAgentHandlingFeeIls(op, {
    columnHandlingFee: order.handlingfee ?? order.handling_fee,
  });
  if (!(hf > 0.009)) return { ...empty, handlingFeeIls: hf };

  const pwc = firstPositive(tf.priceWithCommission, op.priceWithCommission);
  const pwhf = firstPositive(tf.priceWithHandleFee, op.priceWithHandleFee);
  const bags = resolveBaggageIls(order, op, tf);
  const current = round2(Number(total));

  const withoutFee = uniquePositives(
    pwc == null ? [] : bags > 0 ? [pwc, round2(pwc + bags)] : [pwc]
  );
  const withFee = uniquePositives([
    pwhf,
    pwhf != null && bags > 0 ? round2(pwhf + bags) : null,
    pwc != null ? round2(pwc + hf) : null,
    pwc != null && bags > 0 ? round2(pwc + hf + bags) : null,
  ]);

  if (matchesAny(current, withFee)) {
    return { ...empty, total: current, handlingFeeIls: hf };
  }
  if (matchesAny(current, withoutFee)) {
    return {
      total: round2(current + hf),
      addedHandlingFee: true,
      handlingFeeIls: hf,
      sourceSuffix: " + handling_fee",
    };
  }

  return { ...empty, total: current, handlingFeeIls: hf };
}
